import React from "react";
import "./FoundationAdaptationsSection.scss";
import ContentSection from "../components/ContentSection";
import SectionHeader from "../components/SectionHeader";
import TwoColumnLayout from "../components/TwoColumnLayout";

const FoundationAdaptationsSection: React.FC = () => {
  const leftContent = (
    <div className="investment-trends-section-content">
      <p>
        NRWC interviewed several national philanthropic groups to learn how they
        have been affected and are adapting to uncertainty in 2025. The
        philanthropic sector has been filling gaps left by unstable federal
        funding and working to do so strategically. Groups reported deploying
        rapid response funding to help grantees weather immediate disruptions
        due to federal contract cancellations or reimbursement delays. Others
        are doubling down on investments in capacity building, developing
        leadership pipelines, and advocacy skills, viewing these as critical to
        long-term resilience. While core missions have largely remained steady,
        some foundations are leaning into system-focused approaches, investing
        in intermediaries, state-level infrastructure, and workforce ecosystem
        alignment to strengthen the field’s ability to adapt.
      </p>
      <p>
        These shifts reflect a growing recognition that federal uncertainty is
        likely more than a short-term problem. It is a challenge that requires
        deeper and more flexible long-term strategies. Foundations are enhancing
        technical assistance, convenings, and strategic communications for
        grantees, particularly those operating in politically sensitive
        contexts. In some cases, philanthropy is helping organizations frame
        their work in economic terms that resonate across political divides or
        supporting transitions to alternative operating models such as
        registered apprenticeships or, in rare cases, shifting from nonprofit to
        for-profit structures to mitigate political risk. Across the board,
        funders are becoming more vigilant about monitoring federal legislation,
        assessing potential legal exposure, and adjusting their own risk
        tolerance accordingly.
      </p>
    </div>
  );

  const rightContent = (
    <div className="investment-trends-section-right-content">
      <div className="introduction-takeaways investment-takeaways">
        <h3 className="takeaways-heading">TAKEAWAYS</h3>
        <ol className="takeaways-grid">
          <li className="takeaway-item">
            <strong>
              Funders are adapting their own practices to manage risk.
            </strong>
            <p>
              Interviews highlighted that foundations are not only changing how
              they fund, but also how they operate. Interviews revealed that
              funders are paying close attention to federal policy shifts,
              building stronger legal and compliance safeguards, and evaluating
              their own political exposure.
            </p>
          </li>
          <li className="takeaway-item">
            <strong>Philanthropy is focusing on long-term resilience. </strong>
            <p>
              Foundation leaders described moving beyond short-term fixes to
              fill federal gaps, toward investments that strengthen the field’s
              ability to weather uncertainty over time. While rapid response
              funding has helped organizations manage contract disruptions,
              funders are increasingly emphasizing capacity building, and
              systems alignment. This reflects a broader recognition that
              federal instability is likely not a temporary challenge but a more
              long-term reality.
            </p>
          </li>
        </ol>
      </div>
    </div>
  );

  return (
    <ContentSection className="investment-trends-section">
      <SectionHeader
        title="THE PHILANTHROPIC RESPONSE TO UNCERTAINTY: HOW FOUNDATIONS ARE ADAPTING TO THE CHANGING FEDERAL AND TECH LANDSCAPE"
        description="Philanthropy is taking steps to respond to the short-term needs while exercising caution in adapting to the changing landscape"
      />
      <TwoColumnLayout leftContent={leftContent} rightContent={rightContent} />
    </ContentSection>
  );
};

export default FoundationAdaptationsSection;
